import { ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import LoadingSpinner from '@/components/LoadingSpinner';

interface AdminProtectedRouteProps {
  children: ReactNode;
}

const AdminProtectedRoute: React.FC<AdminProtectedRouteProps> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const checkAdminAccess = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          if (isMounted) navigate('/admin/login');
          return;
        }
        
        if (!session?.user) {
          console.log('No session found, redirecting to login');
          if (isMounted) navigate('/admin/login');
          return;
        }

        console.log('Checking admin role for user:', session.user.id);

        // Check if user has admin role
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', session.user.id)
          .in('role', ['admin', 'superadmin']);

        if (roleError) {
          console.error('Role check error:', roleError);
          if (isMounted) navigate('/admin/login');
          return;
        }

        if (!roleData || roleData.length === 0) {
          console.log('No admin role found for user');
          if (isMounted) navigate('/admin/login');
          return;
        }

        console.log('Admin access verified:', roleData);
        if (isMounted) setIsAdmin(true);
      } catch (error) {
        console.error('Error checking admin access:', error);
        if (isMounted) navigate('/admin/login');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    checkAdminAccess();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        navigate('/admin/login');
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <LoadingSpinner size="lg" text="Verifying admin access..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return <>{children}</>;
};

export default AdminProtectedRoute;
